/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import ListPlaceholder from 'src/components/Dashboard/ListPlaceholder';
import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import HTTP404 from 'src/components/HTTP404';
import Loading from 'src/components/Loading';
import { PortfolioList } from 'src/components/Portfolio';
import { APIConfig } from 'src/config';
import { INHIBIT_PORTFOLIO_VIEWS } from 'src/util/constants';

import RouteErrorThrower from './helpers/RouteErrorThrower';
import getFirstPageRetryVariables from './helpers/getFirstPageRetryVariables';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

const messages = defineMessages({
  initialLoadError: {
    id: 'portfolio.portfolio_list.initial_load_error.message',
    defaultMessage: 'An error occurred while loading portfolios. Please try again.',
  },
});

export const portfolioListRouteQuery = graphql`
  query PortfolioListRoute_Query($count: Int, $cursor: String) {
    viewer {
      ...PortfolioList_viewer @arguments(count: $count, cursor: $cursor)
    }
  }
`;

/**
 * Renders `PortfolioList` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @returns {React.ReactNode} portfolio list route content.
 */
function PortfolioListPreloadedQueryRenderer({ routeProps, queryRef }) {
  const queryData = usePreloadedQuery(portfolioListRouteQuery, queryRef);
  const inhibitPortfolioViews = APIConfig().feature(INHIBIT_PORTFOLIO_VIEWS);

  if (inhibitPortfolioViews) {
    return <HTTP404 />;
  }

  return (
    <PortfolioList
      {...routeProps}
      {...queryData}
    />
  );
}

PortfolioListPreloadedQueryRenderer.propTypes = {
  routeProps: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
};

/**
 * Hook-safe renderer for the `PortfolioList` route.
 * Keeps the initial found-relay result for first paint, then switches to a
 * `useQueryLoader`-managed query reference when the user retries after an
 * error so Relay can suspend back to the loading state.
 * The `INHIBIT_PORTFOLIO_VIEWS` kill-switch is resolved here so the 404 guard
 * holds uniformly across the resolved, retry and error paths.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {Error|null} [props.routeError] - Network-level error from found-relay.
 * @returns {React.ReactNode} portfolio list route content.
 */
function PortfolioListRouteRenderer({ routeProps, routeError = null }) {
  const intl = useIntl();
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    portfolioListRouteQuery,
    routeProps,
    { logLabel: 'PortfolioListRoute', getRetryVariablesFunc: getFirstPageRetryVariables },
  );

  const inhibitPortfolioViews = APIConfig().feature(INHIBIT_PORTFOLIO_VIEWS);
  if (inhibitPortfolioViews) {
    return <HTTP404 />;
  }

  return (
    <PlaceholderErrorBoundary
      bodyText={intl.formatMessage(messages.initialLoadError)}
      placeholderComponent={ListPlaceholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {routeError && !queryRef ? (
          <RouteErrorThrower error={routeError} />
        ) : null}
        {queryRef ? (
          <PortfolioListPreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
          />
        ) : (
          <PortfolioList {...routeProps} />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

PortfolioListRouteRenderer.propTypes = {
  routeProps: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  routeError: PropTypes.instanceOf(Error),
};

/**
 * Renders the PortfolioList route state supplied by found-relay.
 * A network error is forwarded to the renderer's `RouteErrorThrower` bridge so
 * the local error boundary can catch it. Authentication is resolved by the
 * owning `CustomerLayout` route, not here.
 * @param {object} readyState - Found-relay route render state.
 * @param {Error|null} readyState.error - Route query error.
 * @param {object} readyState.match - Found route match, available while props are pending.
 * @param {object|null} readyState.props - Resolved route query props.
 * @returns {React.ReactNode} PortfolioList route content or loading state.
 */
export function portfolioListRoute({ error = null, match, props = null }) {
  if (error) {
    const routeProps = props || { match, router: match.router };
    return (
      <PortfolioListRouteRenderer
        routeError={error}
        routeProps={routeProps}
      />
    );
  }

  if (!props) {
    return <Loading />;
  }

  return <PortfolioListRouteRenderer routeProps={props} />;
}

export default PortfolioListRouteRenderer;
